import { useState, useCallback } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { CreateParentEventDialog } from "@/components/parent-events/CreateParentEventDialog";
import { ParentEventRow } from "@/components/parent-events/ParentEventRow";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PillTabs, type PillTabOption } from "@/components/ui/pill-tabs";
import { FilterBar, FilterSearch } from "@/components/ui/filter-bar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableCard } from "@/components/ui/table-card";
import { useParentEvents } from "@/hooks/useParentEvents";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Cykle wydarzeń (parent events) carry a moderation status but never a
// category, so the filter bar mirrors AllEventsPage minus the category select.
const STATUS_OPTIONS: PillTabOption[] = [
  { value: "all", label: "Wszystkie" },
  { value: "pending", label: "Oczekujące" },
  { value: "approved", label: "Zatwierdzone" },
  { value: "rejected", label: "Odrzucone" },
];

export default function ParentEventsPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [limit, setLimit] = useState(25);
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const handleSearchChange = useCallback((v: string) => {
    setSearch(v);
    setCursorStack([]);
  }, []);

  const handleStatusChange = useCallback((v: string) => {
    setStatus(v);
    setCursorStack([]);
  }, []);

  const cursor = cursorStack.at(-1);
  const { data: result, isLoading, error } = useParentEvents({ search, status, cursor, limit });

  const events = result?.data ?? [];
  const hasMore = result?.hasMore ?? false;
  const nextCursor = result?.nextCursor ?? null;

  const handleLimitChange = (v: string) => {
    setLimit(Number(v));
    setCursorStack([]);
  };

  const goNext = () => {
    if (nextCursor) setCursorStack((prev) => [...prev, nextCursor]);
  };
  const goPrev = () => setCursorStack((prev) => prev.slice(0, -1));

  const page = cursorStack.length + 1;

  return (
    <div>
      <PageHeader title="Cykle wydarzeń" action={<CreateParentEventDialog />} />

      <FilterBar>
        <PillTabs value={status} onChange={handleStatusChange} options={STATUS_OPTIONS} />
        <FilterSearch
          value={search}
          onChange={handleSearchChange}
          debounceMs={300}
          placeholder="Szukaj cykli..."
        />
      </FilterBar>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 rounded-xl bg-white animate-pulse border border-black/5" />
          ))}
        </div>
      )}
      {error && <p className="text-destructive">Błąd ładowania: {error.message}</p>}
      {!isLoading && !error && (
        <TableCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[70px]"></TableHead>
                <TableHead>Tytuł</TableHead>
                <TableHead>Okres</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-28">Akcje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-12 text-center text-sm text-muted-foreground"
                  >
                    Brak cykli wydarzeń spełniających kryteria.
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => <ParentEventRow key={event.id} event={event} />)
              )}
            </TableBody>
          </Table>
        </TableCard>
      )}

      {!isLoading && (events.length > 0 || cursorStack.length > 0) && (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Strona {page}</span>
            <Select value={String(limit)} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>na stronę</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={cursorStack.length === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Poprzednia
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} disabled={!hasMore}>
              Następna
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
